import type { ApiKeyScope, ApiKeyState } from '@/entities/integration';

/**
 * Koppelingen: sleutels en hoe je je eigen AI-assistent of software aan Smartkeur
 * hangt. Je-vorm, zoals overal in de app. Voor een keuringsbedrijf in gewone taal;
 * de technische woorden (MCP, Bearer, configbestanden) staan alleen in de
 * ingeklapte stappen per assistent.
 */
export const integrationsCopy = {
    keysTitle: 'Sleutels voor koppelingen',
    keysIntro:
        'Een sleutel is een soort wachtwoord voor één koppeling: je software of AI-assistent komt ermee binnen, met alleen de rechten die je aanvinkt. Hij werkt namens jou en alleen voor dit bedrijf. Wijzig je je wachtwoord, dan stoppen ook je sleutels.',
    platformKeysIntro:
        'Een sleutel voor de beheer-MCP: je AI-agent beheert daarmee alle keuringsbedrijven. Behandel hem als je wachtwoord.',
    empty: 'Nog geen sleutels. Maak er een als je een AI-assistent of je eigen software wilt koppelen.',
    loading: 'Sleutels laden',

    newKey: 'Nieuwe sleutel',
    formTitle: 'Nieuwe API-sleutel',
    name: 'Naam',
    nameHint: 'Waaraan je hem later herkent, bijvoorbeeld "Claude op kantoor".',
    namePlaceholder: 'Claude op kantoor',
    nameRequired: 'Geef de sleutel een naam.',
    scopes: 'Wat mag deze sleutel?',
    scopesRequired: 'Kies minstens één recht.',
    scopesPlatform:
        'Een beheersleutel mag alles in de beheer-MCP: bedrijven, tegoed, abonnementen en het auditlog.',
    expires: 'Geldig tot en met',
    expiresHint: 'Leeg laten: de sleutel werkt tot je hem intrekt.',
    create: 'Sleutel maken',
    creating: 'Sleutel wordt gemaakt…',
    cancel: 'Annuleren',

    secretTitle: (name: string) => `Je nieuwe sleutel "${name}"`,
    secretNote:
        'Kopieer hem nu en zet hem in je AI-assistent of software. Je ziet hem hierna nooit meer; kwijt is intrekken en een nieuwe maken.',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    copyFailed: 'Kopiëren lukte niet. Selecteer de tekst en kopieer hem zelf.',
    secretDone: 'Ik heb hem bewaard',

    states: {
        active: 'Actief',
        expired: 'Verlopen',
        revoked: 'Ingetrokken',
    } satisfies Record<ApiKeyState, string>,
    lastUsed: (when: string) => `Laatst gebruikt ${when}`,
    neverUsed: 'Nog niet gebruikt',
    validUntil: (day: string) => `Geldig t/m ${day}`,
    noEnd: 'Zonder einddatum',
    madeBy: (name: string, day: string) => `Gemaakt door ${name} op ${day}`,
    revokedBy: (name: string, day: string) => `Ingetrokken door ${name} op ${day}`,

    revoke: 'Intrekken',
    revokeLabel: (name: string) => `Sleutel "${name}" intrekken`,
    revokeBody: (name: string) =>
        `"${name}" intrekken? Alles wat deze sleutel gebruikt, krijgt meteen geen toegang meer. Dit kun je niet terugdraaien.`,
    revokeYes: 'Ja, intrekken',
    revoked: (name: string) => `"${name}" is ingetrokken.`,

    activity: 'Wat deed deze sleutel?',
    activityHide: 'Verbergen',
    activityTitle: (name: string) => `Gebruik van "${name}"`,
    activityEmpty: 'Deze sleutel is nog niet gebruikt.',
    activityLoading: 'Gebruik laden',

    scopeLabels: {
        'orders:read': 'Keuringen bekijken en zoeken',
        'orders:write': 'Keuringen aanmaken',
        'planning:read': 'Planning bekijken',
        'planning:write': 'Keuringen inplannen en toewijzen',
        'clients:read': 'Klanten bekijken',
        'clients:write': 'Klanten aanmaken en wijzigen',
        'reports:read': 'Rapporten, bevindingen en klantlinks bekijken',
        'reports:send': 'Rapporten versturen naar de klant',
        'invoices:read': 'Facturen bekijken',
        'credits:read': 'Tegoed bekijken',
    } satisfies Record<ApiKeyScope, string>,

    guideTitle: 'Koppel je AI-assistent',
    guideIntro:
        'Met een AI-assistent zoals Claude of ChatGPT zoek, plan en verstuur je keuringen door gewoon te typen wat je wilt. Je assistent heeft daarvoor het adres hieronder en een sleutel nodig. Klik op je assistent voor de stappen.',
    platformGuideIntro:
        'De beheer-MCP is een aparte server voor platformbeheer. Elke wijziging vraagt een bevestiging en komt in het auditlog, met "via MCP" en jouw naam.',
    mcpAddress: 'Adres voor je assistent (MCP)',
    keyPlaceholderNote:
        'In de voorbeelden staat SK_LIVE_JOUW_SLEUTEL; vervang dat door je sleutel. Net gemaakt? Dan staat hij er al in.',
    claudeDesktop: 'Claude Desktop',
    claudeDesktopHint:
        'Instellingen → Ontwikkelaar → Configuratie bewerken. Zet dit in claude_desktop_config.json en start Claude opnieuw. Node.js moet geïnstalleerd zijn.',
    claudeCode: 'Claude Code',
    claudeCodeHint: 'Eén opdracht in je terminal; daarna staat Smartkeur in /mcp.',
    cursor: 'Cursor en andere assistenten',
    cursorHint:
        'Zet dit in .cursor/mcp.json (of de MCP-instellingen van je client). Elke client die MCP via HTTP met een eigen header ondersteunt, werkt zo.',
    chatgpt: 'ChatGPT',
    chatgptHint:
        'Maak een eigen GPT en voeg een actie toe. Importeer het schema van dit adres en kies bij Authenticatie: API-sleutel, type Bearer, met je sleutel.',
    restTitle: 'Je eigen software',
    restIntro:
        'Laat je eigen software rechtstreeks met Smartkeur werken: alles wat een AI-assistent kan, kan die ook. Geef de documentatie aan wie je software bouwt; daar staat per onderdeel welk recht de sleutel nodig heeft.',
    openDocs: 'Documentatie voor je ontwikkelaar',
    openApi: 'Technische beschrijving (OpenAPI)',
} as const;
